import React, { useState, useEffect, useRef } from "react";
import styles from "./asset/ChatBot.module.scss";
import { chatbotService } from "../../../api/chatbotService";
import PromptEditor from "./PromptEditor";
import { isFeatureEnabled, FEATURES } from "../../../utils/featureFlags";

/**
 * Format message text with basic formatting
 * Supports:
 * - Line breaks (\n)
 * - Bold text (**text**)
 * - Italic text (*text*)
 * - Lists (- item or 1. item)
 *
 * @param {string} text - Raw message text
 * @returns {string} HTML formatted message
 */
const formatMessage = (text) => {
    if (!text) return "";

    // DEBUG: Uncomment these logs when debugging formatting issues
    // console.log("📝 [formatMessage] Raw text:", JSON.stringify(text));

    // Replace line breaks with <br>
    let formatted = text.replace(/\n/g, "<br>");

    // console.log("📝 [formatMessage] After \\n -> <br>:", formatted.substring(0, 500));

    // Fix double spacing for indented bullet points (e.g., "   - Option A:")
    // AI sends: <br><br>   - Option A<br><br>   - Option B
    // We want:  <br>   - Option A<br>   - Option B (single spacing between bullets)
    formatted = formatted.replace(/(<br>){2,}(\s*-\s)/g, "<br>$2");

    // Bold text: **text** -> <strong>text</strong>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    // Italic text: *text* -> <em>text</em>
    formatted = formatted.replace(/\*(.*?)\*/g, "<em>$1</em>");

    // Handle bullet points: add indent and compress spacing
    if (formatted.includes("- ")) {
        // Remove extra <br> between consecutive bullet points
        // Pattern: text<br><br>   - next -> text<br>   - next
        formatted = formatted.replace(/(<br>)(<br>)+(\s*-\s)/g, "$1$3");

        // Add indent to bullet points: "- text" -> "&nbsp;&nbsp;&nbsp;&nbsp;• text"
        // This makes bullets visually indented under their parent numbered step
        formatted = formatted.replace(/<br>(\s*)-\s+/g, "<br>&nbsp;&nbsp;&nbsp;&nbsp;• ");

        // console.log("📝 [formatMessage] After bullet formatting:", formatted.substring(0, 800));
    }

    // Add spacing before numbered items for readability (double <br> between steps)
    formatted = formatted.replace(/(<br>)(\d+\.\s)/g, "$1<br>$2");

    // Compress 3+ consecutive <br> tags into just 2 (to prevent excessive spacing)
    formatted = formatted.replace(/(<br>\s*){3,}/g, "<br><br>");

    // console.log("📝 [formatMessage] Final output:", formatted.substring(0, 500));

    return formatted;
};

/**
 * ChatBot component for providing AI-powered assistance to users
 *
 * @param {Object} props - Component props
 * @param {string} props.questionId - Optional question ID for context-aware responses
 * @param {string} props.confirmedAnswer - User's confirmed answer
 * @returns {JSX.Element} ChatBot component
 */
const ChatBot = ({ questionId, confirmedAnswer }) => {
    // DEBUG: Log confirmedAnswer prop
    console.log("🔍 [ChatBot OLD] confirmedAnswer prop:", confirmedAnswer);
    console.log("🔍 [ChatBot OLD] !!confirmedAnswer:", !!confirmedAnswer);
    // State for user input text
    const [inputText, setInputText] = useState("");

    // State for chat message history
    const [messages, setMessages] = useState([
        {
            id: 1,
            text: "Hello! I'm Prepal Assistant. How can I help you today?",
            sender: "bot",
        },
    ]);

    // Loading state for when waiting for response
    const [isLoading, setIsLoading] = useState(false);

    // Generate a unique session ID for this chat session
    const [sessionId] = useState(Date.now().toString());

    // State for currently streaming message
    const [streamingMessage, setStreamingMessage] = useState("");

    // Reference to the end of messages for auto-scrolling
    const messagesEndRef = useRef(null);

    // State to track if user has selected post-confirm action (Show Answer or Explanation)
    const [hasSelectedPostConfirmAction, setHasSelectedPostConfirmAction] = useState(false);

    // State to track if question has been seeded to Milvus
    const [isSeeded, setIsSeeded] = useState(false);

    // State to show/hide suggested questions
    const [showSuggestions, setShowSuggestions] = useState(true);

    // Ref to store AbortController for canceling streaming
    const abortControllerRef = useRef(null);

    // Language is locked to "en" until multilingual support is implemented
    const language = "en";
    // const [language, setLanguage] = useState(() => {
    //     const saved = localStorage.getItem("prepal_language");
    //     return saved || "vi";
    // });

    // Save language to localStorage when it changes
    // useEffect(() => {
    //     localStorage.setItem("prepal_language", language);
    //     console.log("💾 Language saved to localStorage:", language);
    // }, [language]);

    // Reset suggestions when user confirms answer
    useEffect(() => {
        if (confirmedAnswer && !hasSelectedPostConfirmAction) {
            console.log("🔄 [ChatBot] Confirmed answer detected, showing post-confirm buttons");
            setShowSuggestions(true);
        }
    }, [confirmedAnswer, hasSelectedPostConfirmAction]);

    // Auto-scroll when new messages arrive or streaming updates
    useEffect(() => {
        scrollToBottom();
    }, [messages, streamingMessage]);

    // Function to scroll to bottom of chat
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({
            behavior: "smooth",
        });
    };

    // Effect to handle streaming message updates
    useEffect(() => {
        if (streamingMessage && !isLoading) {
            // Add the complete streamed message to the messages array
            const newBotMessage = {
                id: Date.now(),
                text: streamingMessage,
                sender: "bot",
            };

            setMessages((prev) => [...prev, newBotMessage]);
            setStreamingMessage("");
        }
    }, [streamingMessage, isLoading]);

    // Effect to seed question when component mounts (if questionId is provided)
    useEffect(() => {
        // Only seed if questionId is provided and not already seeded
        const seedQuestion = async () => {
            if (questionId && !isSeeded) {
                try {
                    // Check if it's a valid ObjectId format
                    const isValid = isValidObjectId(questionId);
                    console.log(`🌱 Seeding question ${questionId}... (Valid ID: ${isValid})`);

                    // Always try to seed, even if ID is not standard ObjectId
                    const result = await chatbotService.seedQuestionById(questionId);

                    if (result && result.success) {
                        setIsSeeded(true);
                        console.log(
                            `✅ Question ${questionId} seeded successfully to ${result.collection_name}`,
                        );
                    } else {
                        console.warn(`⚠️ Seeding may have failed: ${JSON.stringify(result)}`);
                        // Could retry or notify user
                    }
                } catch (error) {
                    console.error(`❌ Failed to seed question ${questionId}:`, error);
                    // Retry after 2 seconds if failed
                    setTimeout(seedQuestion, 2000);
                }
            }
        };

        seedQuestion();
    }, [questionId, isSeeded]);

    /**
     * Validate MongoDB ObjectId format
     * @param {string} id - ID to validate
     * @returns {boolean} Whether ID is valid ObjectId format
     */
    const isValidObjectId = (id) => {
        const objectIdPattern = /^[0-9a-fA-F]{24}$/;
        return objectIdPattern.test(id);
    };

    /**
     * Handle clicking a suggested question
     * @param {string} suggestion - The suggested question text
     */
    const handleSuggestionClick = (suggestion) => {
        // Hide suggestions when button is clicked
        setShowSuggestions(false);
        handleSendMessage(suggestion);
    };

    /**
     * Handle post-confirm action (Show Answer or Explanation)
     * After user confirms answer, they can choose to see result or explanation
     * This will close the chatbot after the action is completed
     */
    const handlePostConfirmAction = (actionType) => {
        const message =
            actionType === "answer"
                ? "Show me the full answer key with rationales for all options."
                : "Explain why the correct answer is correct.";

        setShowSuggestions(false);
        setHasSelectedPostConfirmAction(true);

        // Automatically send the message with the text directly
        handleSendMessage(message);
    };

    /**
     * Handle stopping the streaming response
     */
    const handleStopStreaming = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsLoading(false);

        // Save whatever was streamed so far as a message
        if (streamingMessage) {
            const partialMessage = {
                id: Date.now(),
                text: streamingMessage + " [Stopped]",
                sender: "bot",
            };
            setMessages((prev) => [...prev, partialMessage]);
            setStreamingMessage("");
        }
    };

    /**
     * Handle sending a message to the chat API
     * Uses streaming API for real-time responses
     */
    const handleSendMessage = async (messageText = null) => {
        // Use provided messageText or fallback to inputText state
        const textToSend = messageText !== null ? messageText : inputText;
        if (textToSend.trim() === "" || isLoading) return;

        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();

        // Add user message to the chat
        const userMessageText = textToSend;
        const newUserMessage = {
            id: Date.now(),
            text: userMessageText,
            sender: "user",
        };

        setMessages((prev) => [...prev, newUserMessage]);
        setInputText(""); // Always clear input field after sending
        setIsLoading(true);
        setStreamingMessage(""); // Reset streaming message

        try {
            // DEBUG: Log before sending
            console.log("📤 [ChatBot OLD] Sending with answerConfirmed:", !!confirmedAnswer);
            console.log("📤 [ChatBot OLD] Language setting:", language);

            // Use streaming API for real-time response
            await chatbotService.sendMessageStream(
                userMessageText,
                questionId || sessionId, // Use questionId if available, otherwise sessionId
                // Handle each token as it arrives
                (token) => {
                    setStreamingMessage((prev) => prev + token);
                },
                // Handle errors from the stream
                (error) => {
                    console.error("Stream error:", error);
                    setIsLoading(false);

                    // Add error message
                    const errorMessage = {
                        id: Date.now(),
                        text: "Sorry, there was an error processing your request. Please try again.",
                        sender: "bot",
                        isUser: false,
                    };

                    setMessages((prev) => [...prev, errorMessage]);
                },
                // Additional options
                {
                    modelChoice: process.env.REACT_APP_OPENAI_MODEL, // Use env var for model name
                    answerConfirmed: !!confirmedAnswer, // Pass confirmed answer status
                    session_id: sessionId, // Pass session ID for custom prompts
                    signal: abortControllerRef.current?.signal, // Pass abort signal
                    language: language, // Pass language setting (en or vi)
                },
            );

            // Streaming complete
            setIsLoading(false);
            abortControllerRef.current = null;
        } catch (error) {
            console.error("Chat API Error:", error);

            // Fallback to predefined responses if API fails
            const botResponses = [
                "I understand your question. Let's try this approach...",
                "That's a great question! The answer is...",
                "I suggest reviewing this section in the documentation...",
                "This question relates to concepts we covered earlier...",
                "To solve this problem, you need to apply the formula...",
            ];

            const randomResponse = botResponses[Math.floor(Math.random() * botResponses.length)];

            const errorMessage = {
                id: Date.now(),
                text: randomResponse + " (Fallback response - API unavailable)",
                sender: "bot",
                isUser: false,
            };

            setMessages((prev) => [...prev, errorMessage]);
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.askPrepal}>
            <div className={styles.askPrepalChild} />
            <div className={styles.chatContainer}>
                {/* Language Toggle Switch */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        padding: "8px 12px",
                        borderBottom: "1px solid #e5e7eb",
                        backgroundColor: "#f9fafb",
                        gap: "8px",
                    }}
                >
                    <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                        Language:
                    </span>
                    <div
                        // onClick={() => {
                        //     const newLang = language === "vi" ? "en" : "vi";
                        //     console.log("🔄 Language toggle clicked:", language, "→", newLang);
                        //     setLanguage(newLang);
                        // }}
                        style={{
                            position: "relative",
                            display: "inline-flex",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "20px",
                            padding: "2px",
                            cursor: "not-allowed",
                            userSelect: "none",
                            width: "100px",
                            height: "32px",
                            opacity: "0.6",
                        }}
                    >
                        {/* Slider background indicator */}
                        <div
                            style={{
                                position: "absolute",
                                left: language === "vi" ? "2px" : "calc(50% - 2px)",
                                top: "2px",
                                width: "calc(50% - 2px)",
                                height: "calc(100% - 4px)",
                                backgroundColor: "#2563EB", // Prepal blue
                                borderRadius: "18px",
                                transition: "left 0.3s ease",
                            }}
                        />

                        {/* VI Label */}
                        <div
                            style={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: language === "vi" ? "#ffffff" : "#6b7280",
                                zIndex: 1,
                                transition: "color 0.3s ease",
                            }}
                        >
                            VI
                        </div>

                        {/* EN Label */}
                        <div
                            style={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: language === "en" ? "#ffffff" : "#6b7280",
                                zIndex: 1,
                                transition: "color 0.3s ease",
                            }}
                        >
                            EN
                        </div>
                    </div>
                </div>
                <div className={styles.chatMessages}>
                    {messages.map((message) => (
                        <div
                            key={message.id}
                            className={`${styles.message} ${
                                message.sender === "bot" ? styles.botMessage : styles.userMessage
                            }`}
                        >
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: formatMessage(message.text),
                                }}
                            />
                        </div>
                    ))}
                    {isLoading && (
                        <div className={`${styles.message} ${styles.botMessage}`}>
                            {streamingMessage ? (
                                // Show streaming message as it arrives
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: formatMessage(streamingMessage),
                                    }}
                                />
                            ) : (
                                // Show typing indicator when no streaming content yet
                                <div className={styles.typingIndicator}>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            )}
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Suggested Questions - Before Confirm Answer */}
                {showSuggestions && messages.length <= 1 && !confirmedAnswer && (
                    <div className={styles.suggestedQuestionsWrapper}>
                        <div className={styles.suggestionLabel}>Follow up with:</div>
                        <div className={styles.suggestedQuestions}>
                            <button
                                className={styles.suggestionButton}
                                onClick={() =>
                                    handleSuggestionClick(
                                        "Can you give me a hint for this question?",
                                    )
                                }
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 21h6M12 3a6 6 0 0 0-6 6c0 2.5 1.5 4.5 3 6v2a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-2c1.5-1.5 3-3.5 3-6a6 6 0 0 0-6-6z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span>Get a hint</span>
                            </button>
                            <button
                                className={styles.suggestionButton}
                                onClick={() =>
                                    handleSuggestionClick(
                                        "What is the best step-by-step approach to solve this question?",
                                    )
                                }
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 21h6M12 3a6 6 0 0 0-6 6c0 2.5 1.5 4.5 3 6v2a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-2c1.5-1.5 3-3.5 3-6a6 6 0 0 0-6-6z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span>How to approach?</span>
                            </button>
                            <button
                                className={styles.suggestionButton}
                                onClick={() =>
                                    handleSuggestionClick(
                                        "Please help me eliminate the incorrect options for this question.",
                                    )
                                }
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 21h6M12 3a6 6 0 0 0-6 6c0 2.5 1.5 4.5 3 6v2a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-2c1.5-1.5 3-3.5 3-6a6 6 0 0 0-6-6z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span>Eliminating</span>
                            </button>
                            <button
                                className={styles.suggestionButton}
                                onClick={() =>
                                    handleSuggestionClick("Can you explain this concept to me?")
                                }
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span>Explain this concept</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Post-Confirm Actions - After User Confirms Answer */}
                {confirmedAnswer && showSuggestions && !hasSelectedPostConfirmAction && (
                    <div className={styles.suggestedQuestionsWrapper}>
                        <div className={styles.suggestionLabel}>What would you like to see?</div>
                        <div className={styles.suggestedQuestions}>
                            <button
                                className={styles.suggestionButton}
                                onClick={() => handlePostConfirmAction("answer")}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span>Answer Key</span>
                            </button>
                            <button
                                className={styles.suggestionButton}
                                onClick={() => handlePostConfirmAction("explanation")}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span>Show Explanation</span>
                            </button>
                        </div>
                    </div>
                )}

                <div className={styles.chatInputContainer}>
                    <input
                        type="text"
                        className={styles.chatInput}
                        placeholder={
                            isLoading ? "Waiting for response..." : "Ask Prepal a question..."
                        }
                        value={inputText}
                        disabled={isLoading}
                        onChange={(e) => {
                            setInputText(e.target.value);
                            // Only hide pre-confirm suggestions when user types
                            // Don't hide post-confirm buttons
                            if (
                                e.target.value.trim() !== "" &&
                                showSuggestions &&
                                !confirmedAnswer
                            ) {
                                setShowSuggestions(false);
                            }
                        }}
                        onKeyPress={(e) => e.key === "Enter" && !isLoading && handleSendMessage()}
                    />
                    {isLoading ? (
                        // Stop button when loading
                        <svg
                            className={styles.communicationPaperPlane}
                            alt="Stop"
                            onClick={handleStopStreaming}
                            style={{ cursor: "pointer" }}
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
                        </svg>
                    ) : (
                        // Send button when not loading
                        <svg
                            className={styles.communicationPaperPlane}
                            alt="Send"
                            onClick={handleSendMessage}
                            style={{ cursor: "pointer" }}
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    )}
                </div>
            </div>

            {/* Prompt Editor - dev only feature flag */}
            {isFeatureEnabled(FEATURES.SYSTEM_PROMPT_EDITOR) && (
                <PromptEditor sessionId={sessionId} answerConfirmed={confirmedAnswer} />
            )}
        </div>
    );
};

export default ChatBot;
